import type { KidMarker } from '../../../../../../../api/kids/types';
import { addCommasToNumbersAndRound } from '../../../../../../../utilities/numberFormatters';

/** Matches marker tokens embedded in table cells, e.g. `[[FIG:sri]]`. */
export const CELL_MARKER_REGEX = /^\[\[([A-Z_]+):([a-z0-9_]+)\]\]$/;

export type MarkerLookup = Map<string, string>;

/** Build a fast lookup map from marker type + placeholder → value. */
export function buildMarkerLookup(markers: KidMarker[] = []): MarkerLookup {
  const lookup = new Map<string, string>();
  markers.forEach((marker) => {
    lookup.set(`${marker.marker_type}:${marker.placeholder}`, marker.value);
  });
  return lookup;
}

/** Parse FIG:sri API value to a number (0 is valid). */
export function normalizeSriValue(
  raw: string | number | null | undefined,
): number {
  if (raw == null || raw === '') return 0;
  const numeric = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isNaN(numeric)) return 0;
  return numeric;
}

export function toMarkerToken(markerType: string, placeholder: string): string {
  return `[[${markerType}:${placeholder}]]`;
}

export function parseMarkerToken(
  token: string,
): { markerType: string; placeholder: string } | null {
  const match = token.match(CELL_MARKER_REGEX);
  if (!match) return null;
  return { markerType: match[1], placeholder: match[2] };
}

export function getMarkerValue(
  lookup: MarkerLookup,
  markerType: string,
  placeholder: string,
): string | undefined {
  return lookup.get(`${markerType}:${placeholder}`);
}

/**
 * Replace every `[[TYPE:placeholder]]` token in a string with its resolved value.
 * Unmatched tokens are left as-is so the editor can show missing data clearly.
 */
export function resolveMarkerTokensInString(
  value: string,
  lookup: MarkerLookup,
  formatValue?: (
    markerType: string,
    placeholder: string,
    raw: string,
  ) => string,
): string {
  return value.replace(
    /\[\[([A-Z_]+):([a-z0-9_]+)\]\]/g,
    (match, markerType, placeholder) => {
      const raw = lookup.get(`${markerType}:${placeholder}`);
      if (raw == null) return match;
      return formatValue ? formatValue(markerType, placeholder, raw) : raw;
    },
  );
}

/** Round + thousands separators; prefix currency only when present (no EUR fallback). */
export function formatCurrencyValue(
  raw: string,
  currency?: string | null,
): string {
  if (raw === '' || raw == null) return '';
  const numeric = Number(raw);
  if (Number.isNaN(numeric)) return raw;
  const formatted = addCommasToNumbersAndRound(numeric);
  const trimmedCurrency = currency?.trim();
  return trimmedCurrency ? `${trimmedCurrency} ${formatted}` : formatted;
}

/** Marker/API fractions (e.g. 0.0008) → display percent (e.g. 0.08%). */
export function formatPercentValue(raw: string): string {
  if (raw === '' || raw == null) return '';
  const numeric = Number(raw);
  if (Number.isNaN(numeric)) return raw;
  return `${(numeric * 100).toFixed(2)}%`;
}

/** Same as formatPercentValue, with the KID “per year” suffix. */
export function formatPercentPerYearValue(raw: string): string {
  if (raw === '' || raw == null) return '';
  const numeric = Number(raw);
  if (Number.isNaN(numeric)) return raw;
  return `${(numeric * 100).toFixed(2)}% (each year)`;
}

/** Collect markers referenced by marker tokens inside a 2D table. */
export function collectMarkersFromTableContent(
  content: string[][],
  allMarkers: KidMarker[],
): KidMarker[] {
  const referenced = new Set<string>();
  content.forEach((row) => {
    row.forEach((cell) => {
      const parsed = parseMarkerToken(cell);
      if (parsed) {
        referenced.add(`${parsed.markerType}:${parsed.placeholder}`);
      }
    });
  });

  return allMarkers.filter((marker) =>
    referenced.has(`${marker.marker_type}:${marker.placeholder}`),
  );
}
